"use client";

import { useEffect } from "react";

import {
  TOUR_PAGE_HIGHLIGHT_PARAM,
  TOUR_PAGE_SURFACE_ATTR,
  isTourPageSurface,
} from "@tripcart/shared";

/** How long the ring stays before fading. Long enough to find with the eye in a
 *  shrunken preview frame, short enough not to look like page furniture. */
const RING_MS = 2600;

/**
 * Rings one tour-page surface when the URL asks for it.
 *
 * The operator dashboard embeds this page to answer "where does my trust badge
 * actually show up?". It cannot reach into the frame to scroll it — the frame is
 * a different origin — so the ASK travels in the URL and this component does the
 * work from inside.
 *
 * Renders nothing and does nothing at all without the parameter, which is every
 * ordinary visitor: no listener, no observer, no layout read. The cost to the
 * storefront is one `useEffect` that reads `location.search` and returns.
 *
 * Styling is applied imperatively rather than through a class on the target,
 * because the targets are five unrelated components and threading a
 * "highlighted" prop through all of them would put dashboard-only concern into
 * the storefront's own render path.
 */
export function TourPageHighlight(): null {
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get(
      TOUR_PAGE_HIGHLIGHT_PARAM,
    );
    if (!asked || !isTourPageSurface(asked)) return;

    // Two of the five surfaces live inside a departure's collapsed details
    // panel, so they are not in the DOM until something opens it. Nothing else
    // on the page opens it for a visitor who did not ask, and a preview that
    // lands on a closed panel points at nothing.
    const needsExpanding =
      asked === "departure-details" || asked === "whatsapp";

    // The target may be below a lazy boundary, so poll rather than giving up on
    // the first frame. The departures list in particular mounts only after its
    // availability request resolves — expanding once up front fired before the
    // section existed, which is why this lives INSIDE the loop.
    let attempts = 0;
    let ringTimer: number | undefined;

    const tick = window.setInterval(() => {
      attempts += 1;
      const el = document.querySelector<HTMLElement>(
        `[${TOUR_PAGE_SURFACE_ATTR}="${asked}"]`,
      );
      if (!el) {
        if (needsExpanding) {
          const toggle = document.querySelector<HTMLButtonElement>(
            "[data-departure-details-toggle]",
          );
          // Only while collapsed — clicking an open panel would shut it, and on
          // a repeating tick that is a flicker rather than a preview.
          if (toggle?.getAttribute("aria-expanded") === "false") toggle.click();
        }
        if (attempts > 60) window.clearInterval(tick);
        return;
      }
      window.clearInterval(tick);

      el.scrollIntoView({ block: "center", behavior: "auto" });

      const previous = {
        outline: el.style.outline,
        outlineOffset: el.style.outlineOffset,
        borderRadius: el.style.borderRadius,
        transition: el.style.transition,
      };
      el.style.outline = "3px solid var(--tc--preset--color--primary)";
      el.style.outlineOffset = "4px";
      el.style.borderRadius = el.style.borderRadius || "8px";
      el.style.transition = "outline-color 400ms ease";

      ringTimer = window.setTimeout(() => {
        el.style.outline = previous.outline;
        el.style.outlineOffset = previous.outlineOffset;
        el.style.borderRadius = previous.borderRadius;
        el.style.transition = previous.transition;
      }, RING_MS);
    }, 100);

    return () => {
      window.clearInterval(tick);
      if (ringTimer) window.clearTimeout(ringTimer);
    };
  }, []);

  return null;
}
